import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { ToastService } from '@examdojo/core/toast';
import {
  PaymentRequestStatusCellRendererComponent,
  PaymentRequestStatusFilterComponent,
  PayoutRateQuery,
  PayoutRequestQuery,
  PayoutRequestService,
  PayoutRequestStatus,
  PayoutRequestUIModel,
  ReceiptViewerDialogService,
  WorkingHoursDialogResult,
  WorkingHoursDialogService,
} from '@examdojo/payments';
import { ExamdojoSupabaseService } from '@examdojo/supabase';
import { UserQuery } from '@examdojo/user';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { combineLatest, distinctUntilChanged, EMPTY, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { ConfirmationDialogService } from '../../shared/confirmation-dialog/confirmation-dialog.service';
import { ExtraBonusCellRendererComponent } from './extra-bonus-cell-renderer.component';
import { PayoutRequestActionsCellRendererComponent } from './payout-request-actions-cell-renderer.component';
import { PayoutRequestsListService } from './payout-request-list.service';
import { TotalHourlyPayoutCellRendererComponent } from './total-hourly-payout-cell-renderer.component';

@Component({
  selector: 'dojo-payout-requests',
  standalone: true,
  imports: [ButtonComponent, IconComponent, MasterListViewComponent, PaymentRequestStatusFilterComponent],
  templateUrl: './payout-requests.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full flex-col' },
  providers: [provideSimpleListView(PayoutRequestsListService)],
})
export class PayoutRequestsComponent {
  constructor(
    private readonly payoutRequestService: PayoutRequestService,
    private readonly payoutRequestQuery: PayoutRequestQuery,
    private readonly payoutRateQuery: PayoutRateQuery,
    private readonly workingHoursDialogService: WorkingHoursDialogService,
    private readonly receiptViewerDialogService: ReceiptViewerDialogService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly userQuery: UserQuery,
    private readonly dateTimeService: DateTimeService,
    private readonly supabaseService: ExamdojoSupabaseService,
  ) {}

  private static readonly AVAILABLE_MONTHS_COUNT = 3;
  private static readonly EARLIEST_SUBMISSION_DAYS_AFTER_PERIOD = 3;

  private readonly destroyRef = inject(DestroyRef);
  readonly gridApi = signal<GridApi<PayoutRequestUIModel> | null>(null);

  readonly statusFilter = signal<PayoutRequestStatus | 'ALL'>('ALL');

  readonly hourlyRate$ = this.userQuery.active$.pipe(
    distinctUntilChanged((a, b) => a?.id === b?.id),
    switchMap((user) => (user ? this.payoutRateQuery.selectUserRate(user.id) : of(null))),
    map((rate) => rate?.rate ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly state = connectState({
    currentPeriodRequest: this.payoutRequestQuery.selectCurrentPeriodRequest(),
    allPayoutRequests: combineLatest([
      this.payoutRequestQuery.selectCurrentPeriodRequest(),
      this.payoutRequestQuery.selectHistoricalRequests(),
    ]).pipe(
      map(([currentRequest, historicalRequests]) => {
        const allRequests = [...(historicalRequests || [])];
        if (currentRequest) {
          allRequests.unshift(currentRequest);
        }
        return allRequests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }),
    ),
    hourlyRate: this.hourlyRate$,
    canLogWorkingHours: combineLatest([
      this.payoutRequestQuery.selectCurrentPeriodRequest(),
      this.payoutRequestQuery.selectHistoricalRequests(),
    ]).pipe(
      map(([currentRequest, historicalRequests]) => {
        const existingPeriods = new Set<string>();
        if (currentRequest) {
          existingPeriods.add(currentRequest.period);
        }
        historicalRequests.forEach((req) => existingPeriods.add(req.period));

        const today = new Date();
        let availablePeriodsCount = 0;

        for (let i = 0; i < PayoutRequestsComponent.AVAILABLE_MONTHS_COUNT; i++) {
          const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
          const periodStr = this.dateTimeService.__format(date, 'yyyy-MM');

          if (!existingPeriods.has(periodStr)) {
            availablePeriodsCount++;
          }
        }

        return availablePeriodsCount > 0;
      }),
    ),
  });

  readonly columnDefs: Array<ColDef<PayoutRequestUIModel>> = [
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: PaymentRequestStatusCellRendererComponent,
      width: 180,
      pinned: 'left',
    },
    {
      field: 'period',
      headerName: 'Period',
      valueGetter: ({ data }) => (data?.period ? this.getPeriodDisplayName(data.period) : '—'),
      width: 150,
      sortable: true,
      sort: 'desc',
      comparator: (valueA: string, valueB: string, nodeA, nodeB) => {
        const periodA = nodeA.data?.period || '';
        const periodB = nodeB.data?.period || '';

        if (!periodA && !periodB) {
          return 0;
        }
        if (!periodA) {
          return 1;
        }
        if (!periodB) {
          return -1;
        }

        return periodA.localeCompare(periodB);
      },
    },
    {
      field: 'created_at',
      headerName: 'Request Created',
      valueGetter: ({ data }) =>
        data?.created_at
          ? this.dateTimeService.formatDate(new Date(data.created_at), DateTimeFormat.ShortDateTime)
          : '—',
      width: 160,
    },
    {
      field: 'totalHours',
      headerName: 'Hours Worked',
      valueGetter: ({ data }) => {
        const hours = data?.totalHours ?? 0;
        return hours > 0 ? `${hours} hours` : '0 hours';
      },
      sortable: true,
      width: 150,
    },
    {
      field: 'estimatedPayout',
      headerName: 'Total Hourly Payout',
      cellRenderer: TotalHourlyPayoutCellRendererComponent,
      width: 130,
    },
    {
      field: 'extra_bonus_in_eur',
      headerName: 'Bonus',
      cellRenderer: ExtraBonusCellRendererComponent,
      width: 110,
    },
    {
      field: 'calculatedFinalPayout',
      headerName: 'Final Payout',
      valueGetter: ({ data }) => {
        const finalPayout = data?.calculatedFinalPayout;
        return finalPayout !== null && finalPayout !== undefined ? `€${finalPayout.toFixed(2)}` : '—';
      },
      width: 130,
    },
    {
      headerName: 'Actions',
      cellRenderer: PayoutRequestActionsCellRendererComponent,
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      width: 200,
      pinned: 'right',
      cellClass: 'flex h-full items-center justify-end',
    },
  ];

  readonly gridOptions: GridOptions<PayoutRequestUIModel> = {
    pagination: false,
    rowModelType: 'clientSide',
    suppressCellFocus: true,
    suppressContextMenu: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No payout requests found',
      body: 'Start logging your working hours to create a payout request',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      floatingFilter: false,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    getRowId: (params) => (params.data?.id ? `${params.data.id}` : ''),
    isExternalFilterPresent: () => {
      return this.statusFilter() !== 'ALL';
    },
    doesExternalFilterPass: (node) => {
      const selectedStatus = this.statusFilter();
      if (selectedStatus === 'ALL') {
        return true;
      }
      return node.data?.status === selectedStatus;
    },
  };

  onGridReady(event: GridReadyEvent<PayoutRequestUIModel>): void {
    this.gridApi.set(event.api);
    event.api.sizeColumnsToFit();
  }

  updateAgGridStatusFilters(status: PayoutRequestStatus | 'ALL'): void {
    this.statusFilter.set(status);
    this.gridApi()?.onFilterChanged();
  }

  getPeriodDisplayName(period: string): string {
    try {
      return this.payoutRequestService.getPeriodDisplayName(period);
    } catch (error) {
      console.warn('Error formatting period:', period, error);
      return period;
    }
  }

  async openWorkingHoursDialog(request?: PayoutRequestUIModel): Promise<void> {
    const allRequests = this.state.allPayoutRequests;
    const existingRequestPeriods = allRequests.map((r) => r.period);

    let targetRequest: PayoutRequestUIModel | null | undefined;
    let period: string;

    if (request) {
      targetRequest = request;
      period = request.period;
    } else {
      targetRequest = null;
      period = this.findMostRecentAvailablePeriod(existingRequestPeriods);
    }

    // Ensure hourlyRate is not null
    if (this.state.hourlyRate === null) {
      return;
    }

    const result = await this.workingHoursDialogService.openDialog({
      input: {
        period: period,
        existingHours: targetRequest?.hours ?? {},
        isReadonly: false,
        isExistingRequest: !!targetRequest,
        hourlyPaymentRate: this.state.hourlyRate,
        existingRequestPeriods: existingRequestPeriods,
      },
    });

    if (result) {
      this.handleWorkingHoursResult(result, targetRequest);
    }
  }

  async openReceiptViewerDialog(request: PayoutRequestUIModel): Promise<void> {
    const attachments = request.attachments as Array<{ url: string; type: string }> | null | undefined;

    if (!Array.isArray(attachments)) {
      this.toastService.error('Invoice not available');
      return;
    }

    const invoice = attachments.find((att) => att.type === 'invoice');
    if (!invoice?.url) {
      this.toastService.error('Invoice not available');
      return;
    }

    try {
      const s3Url = invoice.url;
      const s3UrlMatch = s3Url.match(/^s3:\/\/([^/]+)\/(.+)$/);

      if (!s3UrlMatch) {
        this.toastService.error('Invalid invoice URL format');
        return;
      }

      const bucketName = s3UrlMatch[1];
      const filePath = s3UrlMatch[2];

      const signedUrl = await this.supabaseService.getSignedUrlForFile(bucketName, filePath);

      await this.receiptViewerDialogService.openDialog({
        input: {
          receiptUrl: signedUrl,
          period: request.period,
        },
      });
    } catch (error) {
      console.error('Error opening invoice:', error);
      this.toastService.error('Failed to load invoice');
    }
  }

  canSubmitRequest(requestId: string): boolean {
    const request = this.state.allPayoutRequests.find((r) => r.id === requestId);
    if (!request) {
      return false;
    }
    return this.canSubmitForPeriod(request.period);
  }

  getSubmitDisabledTooltip(requestId: string): string {
    const request = this.state.allPayoutRequests.find((r) => r.id === requestId);
    if (!request) {
      return '';
    }

    if (this.canSubmitForPeriod(request.period)) {
      return '';
    }

    const earliestDate = this.getEarliestAllowedDate(request.period);
    const formattedDate = this.dateTimeService.formatDate(earliestDate.getTime(), DateTimeFormat.LongDate);

    return `You can only submit for the period of ${request.period} starting from ${formattedDate}`;
  }

  submitPayoutRequest(requestId: string): void {
    // Safety check - button should be disabled, but double-check here
    if (!this.canSubmitRequest(requestId)) {
      return;
    }

    this.payoutRequestService
      .submitPayoutRequest(requestId)
      .pipe(
        tap(() => {
          this.toastService.success('Payout request submitted for approval!');
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  withdrawPayoutRequest(requestId: string): void {
    this.payoutRequestService
      .withdrawPayoutRequest(requestId)
      .pipe(
        tap(() => {
          this.toastService.success('Payout request withdrawn and returned to draft status');
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async deletePayoutRequest(requestId: string): Promise<void> {
    const confirmed = await this.confirmationDialogService.openDialog({
      input: {
        title: 'Delete Payout Request',
        message:
          'You are about to delete this payout request. All logged hours will be permanently removed. This action cannot be undone.',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        confirmColor: 'red',
      },
    });

    if (confirmed) {
      this.payoutRequestService
        .deletePayoutRequest(requestId)
        .pipe(
          tap(() => {
            this.toastService.success('Payout request deleted successfully');
          }),
          this.errorHandlerService.catchHttpErrors(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  private canSubmitForPeriod(period: string): boolean {
    const earliestDate = this.getEarliestAllowedDate(period);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= earliestDate;
  }

  private getEarliestAllowedDate(period: string): Date {
    const periodDate = this.dateTimeService.parseDate(period, 'yyyy-MM');
    const lastDayOfMonth = this.dateTimeService.endOfMonth(periodDate);
    const earliestDate = this.dateTimeService.add(lastDayOfMonth, {
      days: PayoutRequestsComponent.EARLIEST_SUBMISSION_DAYS_AFTER_PERIOD,
    });
    return this.dateTimeService.startOfDay(earliestDate);
  }

  private findMostRecentAvailablePeriod(existingPeriods: string[]): string {
    const today = new Date();

    for (let i = 0; i < PayoutRequestsComponent.AVAILABLE_MONTHS_COUNT; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const periodStr = this.dateTimeService.__format(date, 'yyyy-MM');

      if (!existingPeriods.includes(periodStr)) {
        return periodStr;
      }
    }

    const currentPeriod = this.payoutRequestService.getCurrentPeriod();
    if (!existingPeriods.includes(currentPeriod)) {
      return currentPeriod;
    }

    throw new Error('No available periods found for creating a new payout request');
  }

  private handleWorkingHoursResult(
    result: WorkingHoursDialogResult,
    existingRequest?: PayoutRequestUIModel | null,
  ): void {
    if (existingRequest) {
      this.payoutRequestService
        .updatePayoutRequestWithHours(existingRequest.id, {
          hours: result.hours,
        })
        .pipe(
          tap(() => {
            if (result.shouldSubmit) {
              this.submitPayoutRequest(existingRequest.id);
            } else {
              this.toastService.success('Payout request updated successfully');
            }
          }),
          this.errorHandlerService.catchHttpErrors(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      this.payoutRequestService
        .createPayoutRequest({
          period: result.period,
          hours: result.hours,
        })
        .pipe(
          tap((newRequest) => {
            if (result.shouldSubmit) {
              this.submitPayoutRequest(newRequest.id);
            } else {
              this.toastService.success('Payout request created as draft');
            }
          }),
          this.errorHandlerService.catchHttpErrors(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }
}
